import React, { useState, useEffect, useRef } from "react";

export const TypingAnimation = ({ phrases = [], suggestions = [] }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const typingTimeoutRef = useRef(null);
  const charIndexRef = useRef(0);
  const isDeletingRef = useRef(false);
  const phraseIndexRef = useRef(0);

  useEffect(() => {
    if (phrases.length === 0) return;

    let isCancelled = false;

    charIndexRef.current = 0;
    isDeletingRef.current = false;
    phraseIndexRef.current = 0;
    setDisplayedText("");
    setShowSuggestions(false);
    setCurrentPhraseIndex(0);
    setCurrentSuggestionIndex(0);

    const scheduleNextTick = (delay) => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(type, delay);
    };

    const type = () => {
      if (isCancelled) return;

      const phrase = phrases[phraseIndexRef.current];

      if (!isDeletingRef.current) {
        setDisplayedText(phrase.substring(0, charIndexRef.current));
        charIndexRef.current += 1;

        if (charIndexRef.current > phrase.length * 0.7) {
          setShowSuggestions(true);
          setCurrentSuggestionIndex(0);
        }

        if (charIndexRef.current > phrase.length) {
          isDeletingRef.current = true;
          scheduleNextTick(2000);
          return;
        }

        scheduleNextTick(100);
        return;
      }

      setDisplayedText(phrase.substring(0, charIndexRef.current));
      charIndexRef.current -= 1;
      setShowSuggestions(false);

      if (charIndexRef.current < 0) {
        isDeletingRef.current = false;
        phraseIndexRef.current = (phraseIndexRef.current + 1) % phrases.length;
        setCurrentPhraseIndex(phraseIndexRef.current);
        setCurrentSuggestionIndex(0);
        charIndexRef.current = 0;
      }

      scheduleNextTick(50);
    };

    scheduleNextTick(1000);

    return () => {
      isCancelled = true;
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [phrases]);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(cursorInterval);
  }, []);

  useEffect(() => {
    if (!showSuggestions) return;

    const phraseSuggestions = suggestions[currentPhraseIndex] || [];
    if (phraseSuggestions.length === 0) return;

    const suggestionInterval = setInterval(() => {
      setCurrentSuggestionIndex(
        (prev) => (prev + 1) % phraseSuggestions.length
      );
    }, 1500);

    return () => clearInterval(suggestionInterval);
  }, [showSuggestions, currentPhraseIndex, suggestions]);

  const currentSuggestions = suggestions[currentPhraseIndex] || [];
  const activeSuggestion = currentSuggestions[currentSuggestionIndex] || "";
  const shouldShowSuggestions = showSuggestions && activeSuggestion;

  return (
    <div className="typing-animation-container">
      <div className="typing-text">
        <span className="typed-text">{displayedText}</span>
        <span className={`cursor ${showCursor ? "visible" : ""}`}>|</span>
      </div>
      <div
        className="autocomplete-suggestions"
        style={{
          visibility: shouldShowSuggestions ? "visible" : "hidden",
          opacity: shouldShowSuggestions ? 1 : 0,
        }}
      >
        {currentSuggestions.map((suggestion, idx) => (
          <div
            key={suggestion}
            className={`suggestion-item ${idx === currentSuggestionIndex ? "active" : ""
              }`}
          >
            {suggestion}
          </div>
        ))}
      </div>
    </div>
  );
};
